import React, { useState, useEffect } from 'react';
import './SummaryPage.css';
import PageLayout from './common/PageLayout';
import { useTranslation } from 'react-i18next';
import { returnWithSuccess, getReturnUrl } from '../utils/returnToHost';

function SummaryPage() {
    const { t } = useTranslation();
    const [bankingInfo, setBankingInfo] = useState({ country: null, bank: null });
    const [selectedAccountsData, setSelectedAccountsData] = useState([]);

    const currencySymbols = {
        SEK: 'kr',
        EUR: '€',
        USD: '$',
        GBP: '£',
    };

    function getCurrencySymbol(currency) {
        return currencySymbols[currency] || currency;
    }

    function getExpiryDate() {
        const expiry = new Date();
        expiry.setDate(expiry.getDate() + 180);
        return expiry.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    }

    useEffect(() => {
        const countryData = JSON.parse(sessionStorage.getItem('selectedCountry'));
        const bankData = JSON.parse(sessionStorage.getItem('selectedBank'));
        const accountsData = JSON.parse(sessionStorage.getItem('selectedAccountsData'));

        if (countryData && bankData) {
            setBankingInfo({ country: countryData, bank: bankData });
        }
        if (accountsData) {
            setSelectedAccountsData(accountsData);
        }
    }, []);

    const handleFinish = () => {
        // Try to bounce back to the WC host with ?bankConnected=1 so the
        // invoices table can refresh. Falls back to openbankingfe home when no
        // return URL was stashed (e.g. user reached /summary without going
        // through the OAuth callback in this tab).
        if (!returnWithSuccess()) {
            window.location.assign('/');
        }
    };

    const hasReturnUrl = !!getReturnUrl();
    const navigationProps = {
        previousPath: "/pick-account",
        onNext: handleFinish,
        nextLabel: hasReturnUrl
            ? t('summaryFinishBackToInvoices', 'Finish')
            : t('summaryGoToDashboard'),
    };

    return (
        <PageLayout currentStep={5} navigationProps={navigationProps}
            infoText="summaryInfoText">
            <div className="summary-content">
                <h1 className="summary-title">{t('summaryCongratulations')}</h1>
                <h2 className="summary-subtitle">{t('summaryActive')}</h2>
                <p className="summary-desc">{t('summaryReview')}</p>

                <div className="info-cards-container">
                    {/* Banking card */}
                    <div className="info-card">
                        <h3 className="card-title">{t('summaryBankingHeader')}</h3>
                        {bankingInfo.country && bankingInfo.bank ? (
                            <>
                                <p className="card-content">
                                    <span className={`fi fi-${bankingInfo.country.flagCode}`}></span>
                                    {bankingInfo.country.name}
                                </p>
                                <p className="card-content">{bankingInfo.bank.name}</p>
                            </>
                        ) : (
                            <>
                                <p className="card-content">{t('summaryFallbackCountry')}</p>
                                <p className="card-content">{t('summaryFallbackBank')}</p>
                            </>
                        )}
                    </div>

                    {/* Account card */}
                    <div className="info-card">
                        <h3 className="card-title">{t('summaryAccountHeader')}</h3>
                        {selectedAccountsData.length > 0 ? (
                            selectedAccountsData.map((account, index) => (
                                <div key={`${account.name}-${account.balance}-${index}`}>
                                    <p className="card-content card-content-bold">
                                        {account.name}
                                        {account.isPrimary && (
                                            <span className="primary-badge">
                                                {t('summaryPrimary', 'Primary')}
                                            </span>
                                        )}
                                    </p>
                                    <p className="card-content">
                                        ····{account.accountNumber || ''} · {Number(account.balance).toLocaleString()} {getCurrencySymbol(account.currency)}
                                    </p>
                                </div>
                            ))
                        ) : (
                            <p className="card-content">{t('summaryNoAccounts')}</p>
                        )}
                    </div>
                </div>

                {/* Consent card */}
                <div className="consent-card">
                    <h3 className="card-title">{t('summaryConsentHeader')}</h3>
                    <p className="card-content card-content-bold">
                        ✓ {t('summaryConsentActive')}
                    </p>
                    <p className="card-content consent-expiry">
                        {t('summaryConsentExpiry', { date: getExpiryDate() })} · {t('summaryConsentReverify')}
                    </p>
                </div>
            </div>
        </PageLayout>
    );
}

export default SummaryPage;
